import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Workflow } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import GanttChart from "@/components/project/GanttChart";
import { TASK_STATUSES } from "@/lib/roles";
import { getErrorMessage } from "@/lib/errors";

export default function Schedule() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogTask, setDialogTask] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/schedule-tasks`);
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const syncFromWbs = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/schedule-tasks/sync-from-wbs`);
      toast.success(`Schedule built from WBS: ${data.created} added, ${data.updated} refreshed, ${data.removed} removed`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/schedule-tasks/${id}`);
      toast.success("Task deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <PageHeader
        title="Schedule / Gantt"
        description="Top-level milestone tracker ordered by sequence."
        action={isProjectManager && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={syncFromWbs} disabled={syncing} data-testid="sync-schedule-from-wbs-button"><Workflow className="h-3.5 w-3.5" /> {syncing ? "Building…" : "Build from WBS"}</Button>
            <Button size="sm" onClick={() => setDialogTask({})} data-testid="add-schedule-task-button"><Plus className="h-3.5 w-3.5" /> Add task</Button>
          </div>
        )}
      />

      <GanttChart
        tasks={tasks}
        loading={loading}
        isProjectManager={isProjectManager}
        onEdit={setDialogTask}
        onDelete={remove}
      />

      {dialogTask && <TaskDialog projectId={projectId} tasks={tasks} initial={dialogTask} onClose={() => setDialogTask(null)} onSaved={load} />}
    </div>
  );
}

function TaskDialog({ projectId, tasks, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial.id);
  const [form, setForm] = useState({
    name: initial.name || "", responsibility: initial.responsibility || "", status: initial.status || "NOT_STARTED",
    sort_order: initial.sort_order ?? 0, start_date: initial.start_date || "", end_date: initial.end_date || "",
    actual_start_date: initial.actual_start_date || "", actual_end_date: initial.actual_end_date || "",
    progress_pct: initial.progress_pct ?? 0, depends_on_id: initial.depends_on_id || "none",
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      const payload = {
        ...form, sort_order: Number(form.sort_order) || 0, progress_pct: Number(form.progress_pct) || 0,
        start_date: form.start_date || null, end_date: form.end_date || null,
        actual_start_date: form.actual_start_date || null, actual_end_date: form.actual_end_date || null,
        depends_on_id: form.depends_on_id === "none" ? null : form.depends_on_id,
      };
      if (isEdit) await api.patch(`/projects/${projectId}/schedule-tasks/${initial.id}`, payload);
      else await api.post(`/projects/${projectId}/schedule-tasks`, payload);
      toast.success(isEdit ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="schedule-task-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit task" : "New schedule task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="task-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Responsibility</Label><Input data-testid="task-responsibility-input" value={form.responsibility} onChange={set("responsibility")} className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" data-testid="task-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" /></div>
            <div><Label>End date</Label><Input type="date" data-testid="task-end-date-input" value={form.end_date} onChange={set("end_date")} className="mt-1.5" /></div>
            <div><Label>Actual start</Label><Input type="date" data-testid="task-actual-start-input" value={form.actual_start_date} onChange={set("actual_start_date")} className="mt-1.5" /></div>
            <div><Label>Actual end</Label><Input type="date" data-testid="task-actual-end-input" value={form.actual_end_date} onChange={set("actual_end_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Progress % (0–1)</Label><Input type="number" step="0.01" data-testid="task-progress-input" value={form.progress_pct} onChange={set("progress_pct")} className="mt-1.5" /></div>
            <div>
              <Label>Depends on</Label>
              <Select value={form.depends_on_id} onValueChange={(v) => setForm({ ...form, depends_on_id: v })}>
                <SelectTrigger data-testid="task-depends-on-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {tasks.filter((t) => t.id !== initial.id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button disabled={!form.name} onClick={submit} data-testid="task-submit-button">{isEdit ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
